import React, { useState } from 'react';
import { Building2, CheckCircle, Link2, RefreshCw, XCircle } from 'lucide-react';
import { Badge, Button } from '../../shared/ui';
import {
  AVIS_RESPONSABLE_DOMAINE, avisDecisionDomaine, CLASSE_DOMAINE_A_CONFIRMER, compteARelier, consequenceConfirmation, decisionDomaineAttendue,
  confirmationReserveeEncadrement, demandeConfirmationAdresseDomaine, estParAdresse, explicationDomaine, libelleBadgeDomaine, libelleConfirmation,
  libelleRefus, lireRattachementDomaine, majApresDecision, mentionDomaineConfirme, messageErreurDomaine, nomSociete, peutConfirmerDomaine,
  titreBandeauDomaine, trancherDomaine, type DecisionDomaine,
} from './domaine';

/** Badge « Rattaché par le domaine — à confirmer » : tableau, cartes mobiles
 *  et fiche. Rien si le dossier n'attend pas de confirmation ou s'il est
 *  clos (plus personne ne tranche). */
export const BadgeDomaineAConfirmer: React.FC<{ dossier: unknown; className?: string }> = ({ dossier, className = '' }) => {
  if (!decisionDomaineAttendue(dossier)) return null;
  return (
    <Badge className={`${CLASSE_DOMAINE_A_CONFIRMER} ${className}`}>
      <Link2 className="w-3 h-3" aria-hidden="true" />
      {libelleBadgeDomaine(dossier)}
    </Badge>
  );
};

/** Mention discrète « Société identifiée par le domaine @x.fr », une fois
 *  le rattachement confirmé. Rien sinon. */
export const MentionDomaineConfirme: React.FC<{ dossier: unknown; className?: string }> = ({ dossier, className = '' }) => {
  const r = lireRattachementDomaine(dossier);
  if (!r || r.statut !== 'CONFIRME') return null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs text-graphite ${className}`}>
      <Building2 className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
      {mentionDomaineConfirme(r)}
    </span>
  );
};

// Cibles tactiles d'au moins 40 px sous 1024 px, boutons pleine largeur sur téléphone.
const BOUTON = 'max-lg:min-h-10 max-sm:w-full max-sm:justify-center';

/**
 * Bandeau de la fiche d'un dossier rattaché par le domaine, non confirmé et
 * non clos : dit d'où vient le rattachement et ce qu'entraîne la
 * confirmation. Droits alignés sur le serveur (domaine.ts, en-tête) :
 * « Ce n'est pas cette société » pour tout le personnel ; « Confirmer la
 * société » pour l'encadrement, ou pour tous quand la confirmation ne relie
 * aucun compte client. Quand elle en relie un, le conseiller lit qu'un
 * responsable doit confirmer. Après la décision,
 * `surDecision` reçoit les champs modifiés, à fusionner dans le dossier
 * affiché, et la phrase de confirmation.
 */
export const BandeauDomaineAConfirmer: React.FC<{
  dossier: { id: string; status?: string; rattachementDomaine?: unknown } | object;
  /** Administrateur ou superviseur : seuls à pouvoir confirmer quand la
   *  confirmation relie le dossier à un compte client. */
  encadrement: boolean;
  surDecision: (maj: Record<string, unknown>, avis: string) => void;
}> = ({ dossier, encadrement, surDecision }) => {
  const [enCours, setEnCours] = useState<DecisionDomaine | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const r = lireRattachementDomaine(dossier);
  if (!r || !decisionDomaineAttendue(dossier)) return null;
  const id = (dossier as { id: string }).id;
  const societe = nomSociete(dossier, r);
  const peutConfirmer = peutConfirmerDomaine(dossier, r, encadrement);
  const attendResponsable = !encadrement && confirmationReserveeEncadrement(dossier, r);
  const compte = compteARelier(dossier, r);

  const trancher = async (decision: DecisionDomaine) => {
    setEnCours(decision);
    setErreur(null);
    try {
      let reponse;
      try {
        reponse = await trancherDomaine(id, decision);
      } catch (e) {
        // Adresse du dossier différente de celle du compte : les réponses
        // partiront vers le compte. On le fait confirmer, comme le
        // rattachement manuel (RattacherClientModal).
        if (decision !== 'confirmer' || !demandeConfirmationAdresseDomaine(e)) throw e;
        if (!window.confirm(e.message)) return;
        reponse = await trancherDomaine(id, decision, true);
      }
      surDecision(majApresDecision(decision, r, reponse), avisDecisionDomaine(decision, societe, estParAdresse(r)));
    } catch (e) {
      setErreur(messageErreurDomaine(e));
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div role="region" aria-label={estParAdresse(r) ? 'Client klygroupe.com reconnu' : 'Rattachement par le domaine'} className="mt-3 p-3 rounded-lg border border-sapin-200 bg-sapin-50 text-sapin-900">
      <div className="flex items-start gap-2">
        <Link2 className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0 text-sm">
          <p className="font-semibold">{titreBandeauDomaine(r)}</p>
          <p className="mt-0.5 break-words">{explicationDomaine(dossier, r)}</p>
          <p className="mt-0.5 break-words">{consequenceConfirmation(dossier, r)}</p>
          {compte && <p className="mt-0.5 break-words font-medium">{compte}</p>}
          {attendResponsable
            ? <p className="mt-0.5 font-medium text-sapin-800">{AVIS_RESPONSABLE_DOMAINE}</p>
            : <p className="mt-0.5 text-sapin-800">{estParAdresse(r) ? 'Vérifiez qu\'il s\'agit bien de ce client.' : 'Vérifiez que l\'expéditeur travaille bien pour cette société.'}</p>}
          {erreur && <p role="alert" className="mt-1 font-medium text-[#8A1C16]">{erreur}</p>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 max-sm:flex-col">
        {peutConfirmer && (
          <Button variant="primary" size="sm" className={BOUTON} disabled={enCours !== null} onClick={() => trancher('confirmer')}>
            {enCours === 'confirmer' ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            {libelleConfirmation(r)}
          </Button>
        )}
        <Button variant="secondary" size="sm" className={BOUTON} disabled={enCours !== null} onClick={() => trancher('refuser')}>
          {enCours === 'refuser' ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
          {libelleRefus(r)}
        </Button>
      </div>
    </div>
  );
};
